import { useEffect, useState } from "react";

const NO_OBJECT_URL = { blob: null, url: null };

/**
 * One object URL per blob, revoked when the blob changes or the component goes away. Without the
 * revoke, every image a card has ever shown stays in memory for the life of the tab.
 *
 * The URL is kept together with the blob it was made from, and handed out only while that is
 * still the blob asked for. The effect runs after the render where the blob changes, so on that
 * render the state still holds the previous blob's URL. Handing it out would show the previous
 * node's image on this node's card.
 *
 * The state is emptied when the blob goes away, not merely left unmatched. React Query hands back
 * the same Blob when a node is revisited, and a URL kept for it would already be revoked.
 *
 * Created in an effect rather than a useMemo: StrictMode runs effects twice on mount, which would
 * revoke a memoized URL that is then never made again.
 */
export default function useObjectUrl(blob) {
  const [current, setCurrent] = useState(NO_OBJECT_URL);

  useEffect(() => {
    if (!blob) {
      setCurrent(NO_OBJECT_URL);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setCurrent({ blob, url });
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return blob && current.blob === blob ? current.url : null;
}
